import React from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Slider,
  TextField,
  Chip,
  Alert,
  Paper,
  Divider,
  Tooltip,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StarIcon from "@mui/icons-material/Star";
import SyncIcon from "@mui/icons-material/Sync";
import SmartToyIcon from "@mui/icons-material/SmartToy";

/**
 * Level 2: Quick Controls
 * High-impact toggles and settings that users adjust frequently.
 * Includes: Enable, Broker, Trading Mode, Confidence, Exit Strategy, Execution.
 */
export default function QuickControls({
  settings,
  setSettings,
  handleSettingChange,
  node,
  brokerConnections = {},
  brokerConnectionsLoading = false,
  nextExecutionInfo,
  countdown,
}) {
  const isBoolTrue = (val) => val === "Yes" || val === true;
  const isLeveraged = settings.tradingMode === "margin" || settings.tradingMode === "futures";
  const leverageVal = parseInt(settings.leverage) || 1;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      {/* Next Execution Display */}
      {nextExecutionInfo && (isBoolTrue(settings.enabled)) && (
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 2,
            background:
              nextExecutionInfo.mode === "golden_hours"
                ? "linear-gradient(135deg, #FBBF24 0%, #F97316 100%)"
                : "linear-gradient(135deg, #3b82f6 0%, #3b82f6 100%)",
            color: "white",
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ScheduleIcon />
              <Typography variant="subtitle1" fontWeight="bold">Next Execution</Typography>
            </Box>
            <Chip
              label={
                nextExecutionInfo.mode === "golden_hours" ? (
                  <><StarIcon sx={{ fontSize: 14, mr: 0.5 }} />Golden Hours</>
                ) : (
                  <><SyncIcon sx={{ fontSize: 14, mr: 0.5 }} />Standard</>
                )
              }
              size="small"
              sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">{nextExecutionInfo.time}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {nextExecutionInfo.day}
                {nextExecutionInfo.repeatNumber && nextExecutionInfo.totalRepeats && (
                  <> • Repeat {nextExecutionInfo.repeatNumber}/{nextExecutionInfo.totalRepeats}</>
                )}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <AccessTimeIcon fontSize="small" />
                <Typography variant="h5" fontWeight="bold">{countdown || "--:--"}</Typography>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>countdown</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <Grid container spacing={2}>
        {/* ── Row 1: Enable + Paper Trading ── */}
        <Grid item xs={6}>
          <FormControlLabel
            control={
              <Switch
                checked={isBoolTrue(settings.enabled)}
                onChange={(e) => handleSettingChange("enabled", e.target.checked ? "Yes" : "No")}
                color="primary"
              />
            }
            label={<Tooltip title="Turn the automated scheduler on or off. When enabled, it will scan for trade signals at the configured interval." arrow><span>Enable Scheduler</span></Tooltip>}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            control={
              <Switch
                checked={isBoolTrue(settings.paperTrading)}
                onChange={(e) => handleSettingChange("paperTrading", e.target.checked ? "Yes" : "No")}
                color="warning"
              />
            }
            label={<Tooltip title="Simulate trades without real money. Positions are tracked but no actual orders are placed on the exchange." arrow><span>Paper Trading</span></Tooltip>}
          />
        </Grid>

        {/* ── Row 2: Broker + Trading Mode ── */}
        <Grid item xs={12} md={isLeveraged ? 4 : 6}>
          <FormControl fullWidth size="small">
            <Tooltip title="Select the exchange broker for order execution. Ensure API keys are configured in Settings → API Connections." arrow>
              <InputLabel>Broker</InputLabel>
            </Tooltip>
            <Select
              value={settings.broker || node?.data?.inheritedBroker || "kucoin"}
              onChange={(e) => handleSettingChange("broker", e.target.value)}
              label="Broker"
              disabled={!!node?.data?.inheritedBroker}
            >
              <MenuItem value="kucoin">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: "space-between" }}>
                  <span>KuCoin</span>
                  {brokerConnections.kucoin?.status === "connected" && (
                    <Chip size="small" label="✓" color="success" sx={{ height: 18, fontSize: "0.65rem" }} />
                  )}
                </Box>
              </MenuItem>
              <MenuItem value="binance">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: "space-between" }}>
                  <span>Binance</span>
                  {brokerConnections.binance?.status === "connected" && (
                    <Chip size="small" label="✓" color="success" sx={{ height: 18, fontSize: "0.65rem" }} />
                  )}
                </Box>
              </MenuItem>
              <MenuItem value="alpaca">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: "space-between" }}>
                  <span>Alpaca (Stocks)</span>
                  {brokerConnections.alpaca?.status === "connected" && (
                    <Chip size="small" label="✓" color="success" sx={{ height: 18, fontSize: "0.65rem" }} />
                  )}
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={isLeveraged ? 4 : 6}>
          <FormControl fullWidth size="small">
            <Tooltip title="Spot: buy/sell the asset directly. Margin: borrow funds for leveraged trading. Futures: trade perpetual contracts with leverage." arrow>
              <InputLabel>Trading Mode</InputLabel>
            </Tooltip>
            <Select
              value={settings.tradingMode || "spot"}
              onChange={(e) => handleSettingChange("tradingMode", e.target.value)}
              label="Trading Mode"
            >
              <MenuItem value="general">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "info.main" }} />
                  General (Read-Only)
                </Box>
              </MenuItem>
              <MenuItem value="spot">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "success.main" }} />
                  Spot
                </Box>
              </MenuItem>
              <MenuItem value="margin">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "warning.main" }} />
                  Margin
                </Box>
              </MenuItem>
              <MenuItem value="futures">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "error.main" }} />
                  Futures
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {isLeveraged && (
          <Grid item xs={12} md={4}>
            <Tooltip title="Multiplier applied to your position size. Higher leverage amplifies both profits and losses. Stop-loss is auto-tightened for safety." arrow>
              <FormControl fullWidth size="small">
                <InputLabel>Leverage</InputLabel>
                <Select
                  value={String(settings.leverage || 1)}
                  onChange={(e) => handleSettingChange("leverage", e.target.value)}
                  label="Leverage"
                >
                  {[
                    { value: "1", label: "1×" },
                    { value: "2", label: "2×" },
                    { value: "3", label: "3×" },
                    { value: "5", label: "5×" },
                    { value: "10", label: "10×" },
                    ...(settings.tradingMode === "futures"
                      ? [
                          { value: "20", label: "20×" },
                          { value: "25", label: "25×" },
                          { value: "50", label: "50×" },
                          { value: "75", label: "75×" },
                          { value: "100", label: "100×" },
                        ]
                      : []),
                  ].map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          </Grid>
        )}

        {/* Broker Connection Status */}
        <Grid item xs={12}>
          {(() => {
            const selectedBroker = settings.broker || node?.data?.inheritedBroker || "kucoin";
            const brokerStatus = brokerConnections[selectedBroker];
            if (brokerConnectionsLoading) {
              return (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="caption">Loading API connection status...</Typography>
                </Alert>
              );
            }
            if (brokerStatus?.status === "connected") {
              return (
                <Alert severity="success" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    <strong>{selectedBroker.toUpperCase()} API Connected</strong>
                    {brokerStatus.lastConnected && ` (Last: ${new Date(brokerStatus.lastConnected).toLocaleDateString()})`}
                    {brokerStatus.tradingEnabled && " • Trading Enabled"}
                    {brokerStatus.paperTrading && " • Paper Trading"}
                  </Typography>
                </Alert>
              );
            }
            return (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                <Typography variant="caption">
                  <strong>{selectedBroker.toUpperCase()} API Not Connected</strong> -{" "}
                  <a href="/user/api-connections" style={{ marginLeft: 4, color: "inherit" }}>
                    Configure API Keys →
                  </a>
                </Typography>
              </Alert>
            );
          })()}
        </Grid>

        {/* Leverage warning */}
        {isLeveraged && leverageVal > 1 && (
          <Grid item xs={12}>
            <Alert severity="warning" sx={{ py: 0.5 }}>
              <Typography variant="caption">
                <strong>Leverage {leverageVal}× Active.</strong>{" "}
                {leverageVal >= 10 && "High leverage significantly increases liquidation risk. "}
                {leverageVal >= 50 && "EXTREME RISK. "}
              </Typography>
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* ── Row 3: Confidence + AI Exit Strategy ── */}
        <Grid item xs={12} md={6}>
          <Tooltip title="Minimum AI confidence score required to open a trade. Higher values mean fewer but higher-quality entries. 70-80% is balanced, 90%+ is very selective." arrow>
            <Typography variant="body2" gutterBottom>
              Confidence Threshold: <strong>{settings.confidenceThreshold || 82}%</strong>
            </Typography>
          </Tooltip>
          <Slider
            value={parseInt(settings.confidenceThreshold) || 82}
            onChange={(e, value) => handleSettingChange("confidenceThreshold", value.toString())}
            min={50}
            max={100}
            step={5}
            marks={[
              { value: 50, label: "50%" },
              { value: 70, label: "70%" },
              { value: 90, label: "90%" },
            ]}
            valueLabelDisplay="auto"
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <Tooltip title="Controls how positions are closed. Safe = tight stops & fast protection. Moderate = balanced approach. Max Profit = wider stops, let winners run. Custom = full manual control over every exit toggle." arrow>
              <InputLabel>AI Exit Mode</InputLabel>
            </Tooltip>
            <Select
              value={settings.exitStrategy || "ai_moderate"}
              onChange={(e) => {
                const strategy = e.target.value;
                const modeProfiles = {
                  ai_safe: {
                    useTrailingStop: "Yes", trailingStopAiMode: true,
                    adaptiveProfitTrailEnabled: true, multiTargetTPEnabled: true,
                    zeroLockMeaningfulEnabled: true, aiCloseGuardEnabled: true,
                    trailingStopFloorEnabled: true, intentAutoDetectEnabled: true,
                    fastToGreenTimeoutEnabled: true,
                    stopLossPercent: "0.8", takeProfitPercent: "1.5",
                    rescueScaleInEnabled: true, maxRescueAdds: "2",
                  },
                  ai_moderate: {
                    useTrailingStop: "Yes", trailingStopAiMode: true,
                    adaptiveProfitTrailEnabled: true, multiTargetTPEnabled: true,
                    zeroLockMeaningfulEnabled: true, aiCloseGuardEnabled: true,
                    trailingStopFloorEnabled: true, intentAutoDetectEnabled: true,
                    fastToGreenTimeoutEnabled: false,
                    stopLossPercent: "1.5", takeProfitPercent: "2.5",
                    rescueScaleInEnabled: true, maxRescueAdds: "2",
                  },
                  ai_max_profit: {
                    useTrailingStop: "Yes", trailingStopAiMode: true,
                    adaptiveProfitTrailEnabled: true, multiTargetTPEnabled: false,
                    zeroLockMeaningfulEnabled: true, aiCloseGuardEnabled: false,
                    trailingStopFloorEnabled: true, intentAutoDetectEnabled: true,
                    fastToGreenTimeoutEnabled: false,
                    stopLossPercent: "2.5", takeProfitPercent: "5",
                    rescueScaleInEnabled: true, maxRescueAdds: "3",
                  },
                };
                const profile = modeProfiles[strategy] || {};
                setSettings((prev) => ({ ...prev, exitStrategy: strategy, ...profile }));
              }}
              label="AI Exit Mode"
            >
              <MenuItem value="ai_safe">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ color: "success.main", fontWeight: "bold" }}>🛡️ Safe</Typography>
                  <Typography variant="caption" color="text.secondary">— Tight stops, max protection</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="ai_moderate">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ color: "info.main", fontWeight: "bold" }}>⚖️ Moderate</Typography>
                  <Typography variant="caption" color="text.secondary">— Balanced (recommended)</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="ai_max_profit">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ color: "warning.main", fontWeight: "bold" }}>🚀 Max Profit</Typography>
                  <Typography variant="caption" color="text.secondary">— Let winners run</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="custom">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold">⚙️ Custom</Typography>
                  <Typography variant="caption" color="text.secondary">— Manual control</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Exit mode description */}
        <Grid item xs={12}>
          {settings.exitStrategy === "ai_safe" && (
            <Alert severity="success" sx={{ py: 0.25 }}>
              <Typography variant="caption">
                <strong>Safe:</strong> Tight trailing, partial TP, fast-to-green timeout, quick zero-lock. SL {settings.stopLossPercent || "0.8"}% / TP {settings.takeProfitPercent || "1.5"}% safety caps.
              </Typography>
            </Alert>
          )}
          {(settings.exitStrategy === "ai_moderate" || !settings.exitStrategy) && (
            <Alert severity="info" sx={{ py: 0.25 }}>
              <Typography variant="caption">
                <strong>Moderate:</strong> Adaptive trailing, partial TP, zero-lock. SL {settings.stopLossPercent || "1.5"}% / TP {settings.takeProfitPercent || "2.5"}% safety caps. Recommended.
              </Typography>
            </Alert>
          )}
          {settings.exitStrategy === "ai_max_profit" && (
            <Alert severity="warning" sx={{ py: 0.25 }}>
              <Typography variant="caption">
                <strong>Max Profit:</strong> Wide trailing, no partial TP, no AI guard. SL {settings.stopLossPercent || "2.5"}% / TP {settings.takeProfitPercent || "5"}%. Higher drawdown risk.
              </Typography>
            </Alert>
          )}
          {settings.exitStrategy === "custom" && (
            <Alert severity="warning" sx={{ py: 0.25 }}>
              <Typography variant="caption">
                <strong>Custom:</strong> Full manual control. Advanced controls below. Conflicting settings may cause issues.
              </Typography>
            </Alert>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* ── Row 4: Auto-Execute + Scan Interval + Max Entries ── */}
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={isBoolTrue(settings.autoExecute)}
                onChange={(e) => {
                  const isAuto = e.target.checked;
                  setSettings({
                    ...settings,
                    autoExecute: isAuto ? "Yes" : "No",
                    confirmationMode: isAuto ? "No" : "Yes",
                  });
                }}
                color="success"
                size="small"
              />
            }
            label={
              <Box>
                <Tooltip title="When ON, trades are executed automatically without manual confirmation. When OFF, signals appear in Trade History for manual review with a countdown timer." arrow>
                  <Typography variant="body2">Auto-Execute</Typography>
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                  {isBoolTrue(settings.autoExecute) ? "Auto" : "Signal mode"}
                </Typography>
              </Box>
            }
          />
          {/* v4.4.3: Signal Validity Time — only visible when Auto-Execute is OFF */}
          {!isBoolTrue(settings.autoExecute) && (
            <Box sx={{ mt: 1 }}>
              <Tooltip title="How long signals remain valid in Trade History before auto-expiring. Shorter = more urgent decisions." arrow>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Signal Validity</InputLabel>
                  <Select
                    value={settings.signalValidityMinutes || 30}
                    onChange={(e) => handleSettingChange("signalValidityMinutes", Number(e.target.value))}
                    label="Signal Validity"
                  >
                    <MenuItem value={1}>1 min</MenuItem>
                    <MenuItem value={2}>2 min</MenuItem>
                    <MenuItem value={3}>3 min</MenuItem>
                    <MenuItem value={4}>4 min</MenuItem>
                    <MenuItem value={5}>5 min</MenuItem>
                    <MenuItem value={10}>10 min</MenuItem>
                    <MenuItem value={15}>15 min</MenuItem>
                    <MenuItem value={30}>30 min</MenuItem>
                    <MenuItem value={60}>1 hour</MenuItem>
                    <MenuItem value={120}>2 hours</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <Tooltip title="How often the AI scans markets for new trade signals. Shorter intervals catch opportunities faster but use more API calls." arrow>
              <InputLabel>Scan Interval</InputLabel>
            </Tooltip>
            <Select
              value={settings.scanInterval || "5m"}
              onChange={(e) => handleSettingChange("scanInterval", e.target.value)}
              label="Scan Interval"
            >
              <MenuItem value="1m">1 min</MenuItem>
              <MenuItem value="2m">2 min</MenuItem>
              <MenuItem value="5m">5 min</MenuItem>
              <MenuItem value="10m">10 min</MenuItem>
              <MenuItem value="15m">15 min</MenuItem>
              <MenuItem value="30m">30 min</MenuItem>
              <MenuItem value="1h">1 hour</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Max Entries / Scan"
            type="number"
            value={settings.maxSignalsPerRun || "5"}
            onChange={(e) => handleSettingChange("maxSignalsPerRun", e.target.value)}
            inputProps={{ min: 1, max: 50 }}
            helperText="Max new positions per scan cycle"
          />
        </Grid>

        {/* Skip open symbols */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={isBoolTrue(settings.skipOpenSymbols)}
                onChange={(e) => handleSettingChange("skipOpenSymbols", e.target.checked ? "Yes" : "No")}
                color="info"
                size="small"
              />
            }
            label={
              <Tooltip title="When enabled, the scanner will not open a new position on a symbol that already has an open position. Prevents doubling up on the same asset." arrow>
                <Typography variant="body2">
                  Skip symbols with open positions
                </Typography>
              </Tooltip>
            }
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
